import React, { useContext, useMemo } from 'react';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import { useLastValidRefinanceValues, useRefinanceColors, abbr } from './RefinanceCalculator.utils.js';
import PropInterpolatorWrapper from '../PaymentDonutChart/Chart/common/PropInterpolator.js';
import withDimensions from '../PaymentDonutChart/withDimensions.js';
import { getWholeDollars } from '../../utils/zhl-calculator.js';
import LineChart from '../PaymentDonutChart/LineChart/LineChart.js';

var CHART_NAME = 'Total Savings';
var monthFormatter = function monthFormatter(num) {
  return num + " month" + (num > 1 ? 's' : '');
};
var RefinanceLineGraphBase = function RefinanceLineGraphBase(_ref) {
  var containerWidth = _ref.containerWidth;
  var values = useLastValidRefinanceValues();
  var _colors = useRefinanceColors();
  var theme = useContext(ThemeContext);

  // schedule={savingsSchedule}
  // lifetimeSavingsColor={DEFAULT_COLORS.lifetimeSavingsColor}
  // infoPointColor={DEFAULT_COLORS.infoPointColor}

  var prepareData = function prepareData() {
    var schedule = values.calculations.savingsSchedule;
    var dataValues = [];

    // Add additional info points to the line graph, the points are
    // different series at the same position on the line
    for (var i = 0; i < schedule.length; i += 1) {
      var months = monthFormatter(schedule[i].month);
      var dataValue = {
        x: i,
        y: schedule[i].totalSavings
      };
      if (schedule[i].breakEven) {
        dataValue.info = "This is your break even point. After " + months + ", your total savings will be greater than any costs to refinance your loan.";
        dataValue.color = _colors.infoPointColor;
      }
      if (schedule[i].breakNegative) {
        dataValue.info = "After this point, you will no longer be saving money. Unless there is a break point after " + months + ", then refinancing is not a good idea.";
        dataValue.color = _colors.infoPointColor;
      }
      // Don't show complete if it is the last data point
      if (schedule[i].currentLoanComplete && i !== schedule.length - 1) {
        dataValue.info = "After " + months + " you will have paid off your previous loan and all additional payments to your new loan will decrease your total savings.";
        dataValue.color = _colors.infoPointColor;
      }
      if (schedule[i].newLoanComplete && i !== schedule.length - 1) {
        dataValue.info = "After " + months + " you will have paid off your new loan. From this point forward you will continue to save money until your old loan is paid off.";
        dataValue.color = _colors.infoPointColor;
      }
      dataValues.push(dataValue);
    }
    return [{
      name: CHART_NAME,
      formatter: function formatter(value) {
        return (
          // eslint-disable-next-line no-restricted-globals
          isNaN(value) || value === null ? null : getWholeDollars(value)
        );
      },
      values: dataValues
    }];
  };
  var lineData = useMemo(prepareData, [values.calculations.savingsSchedule, _colors.infoPointColor]);
  var legendIndexFormatter = function legendIndexFormatter(x) {
    if (x === null) {
      return null;
    }
    var isPlural = x > 1;
    var suffix = "month" + (isPlural ? 's' : '');
    return x + " " + suffix;
  };
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      paddingTop: '60px'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontSize: '10px',
      height: '245px'
    }
  }, /*#__PURE__*/React.createElement(LineChart, {
    containerWidth: containerWidth,
    legend: false,
    legendIndexFormatter: legendIndexFormatter,
    margins: {
      top: 10,
      right: 40,
      bottom: 40,
      left: 20
    },
    data: lineData,
    colors: function colors() {
      return _colors.lifetimeSavingsColor;
    },
    xHideOrigin: true,
    xAxisTickCount: 3,
    xAxisFormatter: function xAxisFormatter(x) {
      return x + " mo";
    },
    yAxisFormatter: function yAxisFormatter(x) {
      return "$" + abbr(x);
    },
    yAxisTickCount: 4,
    lineWidth: 2,
    height: 245,
    axesColor: theme.constellation.colors.backgroundLight,
    tickTextStroke: theme.constellation.colors.textLight,
    displayDataPoints: false,
    xLabel: "When",
    yLabel: CHART_NAME
  })));
};
RefinanceLineGraphBase.defaultProps = {};
RefinanceLineGraphBase.propTypes = process.env.NODE_ENV !== "production" ? {
  containerWidth: PropTypes.number.isRequired
} : {};

// Width is the only property necessary for the dimensions to be set for the line chart
// This line chart is only displayed on desktop, and the default width provided is the rendered width
// of the chart on our most common desktop resolution (1920x1080) as of 10/6/2021
var RefinanceLineGraph = PropInterpolatorWrapper(withDimensions(RefinanceLineGraphBase, {
  defaultWidth: 980,
  handleHeight: false
}));
var RefinanceLineGraph$1 = RefinanceLineGraph;

export { RefinanceLineGraph$1 as default };
